import { createBrowserRouter, Navigate } from 'react-router';
// FSD: 계층 배럴(@/pages) 금지 — 슬라이스별 public API 로 직접 접근한다.
import { BannersPage } from '@/pages/banners';
import { ComingSoonPage } from '@/pages/coming-soon';
import { DashboardPage } from '@/pages/dashboard';
import { ExhibitionsPage } from '@/pages/exhibitions';
import { LandingPage } from '@/pages/landing';
import { MasterDataPage } from '@/pages/master-data';
import { NotificationSettingsPage } from '@/pages/notification-settings';
import { NotificationsPage } from '@/pages/notifications';
import { OrderDetailPage } from '@/pages/order-detail';
import { OrderInquiriesPage, OrderInquiryDetailPage } from '@/pages/order-inquiries';
import { OrdersPage } from '@/pages/orders';
import { PaymentDetailPage, PaymentsPage } from '@/pages/payments';
import { PortfolioPage } from '@/pages/portfolio';
import { PortfolioEditorPage } from '@/pages/portfolio-editor';
import { ProductEditAreaPage } from '@/pages/product-edit-area';
import { ProductionOrderPrintPage } from '@/pages/production-order-print';
import { ProductionOrdersPage } from '@/pages/production-orders';
import { ProductEditorPage, ProductsPage } from '@/pages/products';
import { QuoteDetailPage, QuotesPage } from '@/pages/quotes';
import { ReviewDetailPage, ReviewsPage } from '@/pages/reviews';
import { ShippingFeeSettingsPage } from '@/pages/shipping-fee-settings';
import { SignInPage } from '@/pages/sign-in';
import { SiteSettingsPage } from '@/pages/site-settings';
import { SupplierSettingsPage } from '@/pages/supplier-settings';
import { UserDetailPage, UsersPage } from '@/pages/users';
import { ADMIN_GNB_SECTIONS, DEFAULT_PATH } from '@/shared/config';
import { AdminLayout } from './admin-layout';
import { RequireAdmin } from './require-admin';

/** 실제 페이지가 붙은 GNB 경로 — 준비중 라우트 자동 생성에서 제외한다. */
const IMPLEMENTED_PATHS = new Set([
  '/dashboard',
  '/users',
  '/master-data',
  '/exhibitions',
  '/products',
  '/products/editor',
  '/landing',
  '/banners',
  '/orders',
  '/production-orders',
  '/payments',
  '/order-inquiries',
  '/portfolio',
  '/quotes',
  '/quotes/history',
  '/reviews',
  '/notifications',
  '/notifications/settings',
  '/settings/shipping-fee',
  '/settings/site',
  '/settings/supplier',
]);

export const router = createBrowserRouter([
  { path: '/sign-in', element: <SignInPage /> },

  // 관리자 전용 — 비로그인 · 일반 계정은 가드가 거른다.
  {
    element: <RequireAdmin />,
    children: [
      // 발주서 인쇄 전용 — 가드 안이지만 머리글·사이드바(AdminLayout) 없이 문서만 보인다.
      { path: '/orders/:orderId/production-order/print', element: <ProductionOrderPrintPage /> },
      {
        element: <AdminLayout />,
        children: [
          { path: '/', element: <Navigate to={DEFAULT_PATH} replace /> },
          // 대시보드 — 기본 진입 화면. 처리 대기·기간 현황·미처리 상담·주의 필요 주문·인기 상품(ADMIN 만).
          { path: '/dashboard', element: <DashboardPage /> },
          // 회원 — 목록과 상세(계정 정보·로그인 수단·활동). 차단·해제는 목록에서 한다.
          { path: '/users', element: <UsersPage /> },
          { path: '/users/:userId', element: <UserDetailPage /> },
          { path: '/master-data', element: <MasterDataPage /> },
          { path: '/exhibitions', element: <ExhibitionsPage /> },
          { path: '/landing', element: <LandingPage /> },
          { path: '/banners', element: <BannersPage /> },
          { path: '/portfolio', element: <PortfolioPage /> },
          { path: '/portfolio/new', element: <PortfolioEditorPage /> },
          { path: '/portfolio/:id/edit', element: <PortfolioEditorPage /> },
          { path: '/products', element: <ProductsPage /> },
          { path: '/products/editor', element: <ProductEditorPage /> },
          { path: '/products/editor/:productId', element: <ProductEditorPage /> },
          { path: '/products/editor/:productId/edit-area', element: <ProductEditAreaPage /> },
          // 주문(주문 설계 §8-5·§8-6). 상세 경로는 취소 요청 접수 메일의 관리자 링크(worker, `${APP_ADMIN_URL}/orders/{주문ID}`)와 같아야 한다.
          { path: '/orders', element: <OrdersPage /> },
          { path: '/orders/:orderId', element: <OrderDetailPage /> },
          { path: '/production-orders', element: <ProductionOrdersPage /> },
          // 교환·환불 문의. 상세 경로는 접수 메일의 관리자 링크(worker, `${APP_ADMIN_URL}/order-inquiries/{id}`)와 같아야 한다.
          { path: '/order-inquiries', element: <OrderInquiriesPage /> },
          { path: '/order-inquiries/:inquiryId', element: <OrderInquiryDetailPage /> },
          { path: '/payments', element: <PaymentsPage /> },
          { path: '/payments/:paymentId', element: <PaymentDetailPage /> },
          // 견적(견적 설계 §13). 상세 경로는 새 요청 알림 메일의 링크(worker adminLinkOf)와 같아야 한다.
          { path: '/quotes', element: <QuotesPage view="requests" /> },
          { path: '/quotes/history', element: <QuotesPage view="history" /> },
          { path: '/quotes/:quoteRequestId', element: <QuoteDetailPage /> },

          // 후기 관리 — 목록과 상세.
          { path: '/reviews', element: <ReviewsPage /> },
          { path: '/reviews/:reviewId', element: <ReviewDetailPage /> },

          // 알림 발송 이력(알림 설계 §6-1).
          { path: '/notifications', element: <NotificationsPage /> },
          // 알림 설정(알림 설계 §6-2) — 운영자 수신자·유형별 ON/OFF.
          { path: '/notifications/settings', element: <NotificationSettingsPage /> },
          // 배송비 설정(SUPER-86 D9) — 기본·제주·도서산간 금액. ADMIN 만(RequireAdmin + 서버 adminProcedure).
          { path: '/settings/shipping-fee', element: <ShippingFeeSettingsPage /> },
          // 운영 설정 — 카카오톡 채널 주소(고객용 웹 상담 버튼). ADMIN 만.
          { path: '/settings/site', element: <SiteSettingsPage /> },

          { path: '/settings/supplier', element: <SupplierSettingsPage /> },

          // GNB 준비중 페이지 — 메뉴 구성은 shared/config/gnb.ts 가 단일 원천이다.
          // 실제 페이지가 생기면 IMPLEMENTED_PATHS 에 넣고 위에 라우트를 추가한다.
          ...ADMIN_GNB_SECTIONS.flatMap((section) => section.items)
            .filter((item) => !IMPLEMENTED_PATHS.has(item.path))
            .map((item) => ({
              path: item.path,
              element: <ComingSoonPage title={item.label} />,
            })),
        ],
      },
    ],
  },
]);
